import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Heart, Users, Award } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/sections/CTASection";

export default function About() {
  return (
    <>
      <SEO
        title="À propos — Organisme de formation IA"
        description="YAFORMIS est un organisme de formation certifié Qualiopi spécialisé en intelligence artificielle. Découvrez notre équipe, nos valeurs et notre méthode."
        path="/a-propos"
      />
      <section className="bg-brand-page py-20 md:py-32 border-b border-brand-line" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="overline">À propos</div>
              <h1 className="font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.95] text-brand-ink">
                Former une génération<br />
                <span className="text-brand-blue">IA-ready.</span>
              </h1>
              <p className="mt-6 text-xl text-slate-600 leading-relaxed max-w-2xl">
                YAFORMIS est un organisme de formation professionnelle dédié à l'intelligence artificielle, créé par des praticiens pour des praticiens.
              </p>
            </div>
            <div className="lg:col-span-5">
              <img
                src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                alt="Formateur YAFORMIS présentant à un groupe en formation IA"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-surface py-14 md:py-20" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">Notre mission</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
              Rendre l'IA accessible aux professionnels de tous horizons.
            </h2>
          </div>
          <div className="lg:col-span-7 text-base md:text-lg text-slate-600 leading-relaxed space-y-4">
            <p>L'intelligence artificielle change radicalement les métiers. Pourtant, 80 % des professionnels la considèrent encore comme une boîte noire intimidante. Nous croyons que <strong className="text-brand-ink">tout le monde peut maîtriser l'IA, à condition d'être formé par les bonnes personnes</strong>.</p>
            <p>Nos formations ne sont pas conçues par des théoriciens, mais par des consultants, ingénieurs et fondateurs qui utilisent l'IA chaque jour pour leurs clients. Chaque module est testé sur le terrain avant d'être enseigné.</p>
            <p>Notre engagement : <strong className="text-brand-ink">vous rendre opérationnels</strong>, pas seulement informés.</p>
          </div>
        </div>
      </section>

      <section className="bg-brand-page py-14 md:py-20" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline">Valeurs</div>
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05] text-brand-ink">
            Quatre principes qui guident chaque formation.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
            {[
              { icon: Target, title: "Pragmatisme", desc: "Pas de théorie creuse. Chaque concept est ancré dans un cas d'usage métier concret et applicable." },
              { icon: Heart, title: "Bienveillance", desc: "Aucune question n'est bête. Nous accueillons les débutants comme les experts, sans jamais infantiliser." },
              { icon: Users, title: "Humain au centre", desc: "L'IA est un outil au service de l'humain. Nous formons à l'IA, pas à la remplacer par l'IA." },
              { icon: Award, title: "Excellence", desc: "Note moyenne de 4.9/5. Programme mis à jour tous les trimestres. Formateurs choisis sur portfolio." },
            ].map((v) => (
              <div key={v.title} className="bg-white p-8 md:p-10" data-testid={`value-${v.title.toLowerCase()}`}>
                <v.icon className="w-8 h-8 text-brand-blue" strokeWidth={1.6} />
                <h3 className="font-heading font-bold text-2xl mt-5 text-brand-ink">{v.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-ink text-white py-14 md:py-20 relative overflow-hidden" data-testid="about-trainers">
        <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <div className="overline" style={{ color: "#FF4500" }}>Formateurs</div>
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
            Une équipe de praticiens IA en activité.
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-300 max-w-3xl leading-relaxed">
            Tous nos formateurs cumulent au minimum cinq ans d'expérience IA en entreprise. Ils interviennent comme consultants ou tech leads chez nos clients, en parallèle de leurs missions de formation.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Direction pédagogique", role: "Architecte IA · ex-Lead Data", years: "12+", focus: "LLM, agents, gouvernance IA" },
              { name: "Pôle outils & productivité", role: "Consultant senior IA appliquée", years: "8+", focus: "ChatGPT, Claude, automation" },
              { name: "Pôle technique avancé", role: "Ingénieur ML · contributeur open source", years: "10+", focus: "LangChain, fine-tuning, RAG" },
            ].map((t) => (
              <div key={t.name} className="bg-white/[0.03] border border-white/10 p-7 hover:border-brand-cta transition-colors" data-testid={`trainer-${t.name.replace(/\s/g, '-').toLowerCase()}`}>
                <div className="num-marker text-brand-cta">{t.years} ans</div>
                <h3 className="font-heading font-bold text-2xl mt-3">{t.name}</h3>
                <div className="text-sm text-slate-400 mt-1">{t.role}</div>
                <div className="mt-5 pt-5 border-t border-white/10 text-sm text-slate-300">
                  Spécialités : {t.focus}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/contact" className="btn-cta" data-testid="about-trainers-cta">
              Rencontrer un formateur
              <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
